import { useEffect, useState } from "react";
import ContentsTopHeader from "../../../components/common/contents.top.header";
import {
  BackupFolderContainer,
  ContentsBody,
} from "../../../style/contents/backup/backup";
import { useLocation, useParams } from "react-router-dom";
import {
  fetchBackupFolderList,
  fetchBackupFolderListUser,
  fetchBackupUserListLoad,
  fetchFilePathDelete,
} from "../../../api/admin";
import { BackupUser } from "./backup.user";
import { fetchLoadClientLogin } from "../../../api/auth";
import FolderList from "../../../components/backup/folder.list";
import { useAuthStore } from "../../../atoms/user.auth.store";

type BackupUserList = BackupUser[];

export interface BackupFolder {
  createDate: string;
  name: string;
  rootDirectoryPath: string;
}

const BackupFolderList = () => {
  const { role } = useAuthStore(); // ✅ role 가져오기

  const [userData, setUserData] = useState<BackupUserList>([]);
  const [loginToken, setloginToken] = useState("");
  const [folderList, setFolderList] = useState<BackupFolder[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);

  const location = useLocation();
  const { deviceId } = useParams<{ deviceId: string }>();
  const queryParams = new URLSearchParams(location.search);

  const userName = decodeURIComponent(atob(queryParams.get("name") || ""));
  const department = decodeURIComponent(
    atob(queryParams.get("department") || "")
  );
  const pcName = decodeURIComponent(atob(queryParams.get("pcName") || ""));

  const id = queryParams.get("id");
  const userAccount = queryParams.get("userAccount");

  useEffect(() => {
    const loadUserList = async () => {
      const userId = localStorage.getItem("userId");
      const companyCode = localStorage.getItem("companyCode");

      const response = await fetchBackupUserListLoad(
        Number(userId),
        companyCode || ""
      );
      setUserData(response.userList);
    };
    loadUserList();
  }, []);

  useEffect(() => {
    const loadUserLogin = async () => {
      const companyCode = localStorage.getItem("companyCode");

      // ✅ userData 배열에서 userAccount가 일치하는 항목 찾기
      const matchedUser = userData.find(
        (user) => user.userAccount === userAccount
      );

      if (!matchedUser) {
        console.warn("해당 userAccount에 대한 유저 정보를 찾을 수 없습니다.");
        return;
      }

      const password = matchedUser.password;

      const requestData = {
        companyCode: companyCode || "",
        userAccount: matchedUser.userAccount,
        password: password,
        deviceId: "", // 백업용 deviceId는 이후 필요 시 추가
      };
      console.log(requestData);

      try {
        const response = await fetchLoadClientLogin(requestData);

        console.log(response);
        setloginToken(response.jwtToken);
      } catch (err) {
        console.error("🚨 로그인 실패:", err);
      }
    };

    // 🔒 ✅ admin이고 userData가 존재하며 id가 있을 때만 실행
    if (role === "admin" && userData.length > 0 && id) {
      loadUserLogin();
    }
  }, [userData]);

  // ✅ Admin 전용 폴더 로딩
  useEffect(() => {
    const loadFolderList = async () => {
      try {
        const response = await fetchBackupFolderList(
          Number(id),
          deviceId || "",
          loginToken || ""
        );
        setFolderList(response.message.rootDirectoryList);
      } catch (err) {
        console.error("❌ Admin 폴더 불러오기 실패:", err);
      }
    };

    if (role === "admin" && loginToken) {
      loadFolderList();
    }
  }, [loginToken, role, id, deviceId]);

  // ✅ User 전용 폴더 로딩
  useEffect(() => {
    const loadFolderListUser = async () => {
      try {
        const res = await fetchBackupFolderListUser(Number(id), deviceId || "");
        setFolderList(res.message.rootDirectoryList);
      } catch (err) {
        console.error("❌ User 폴더 불러오기 실패:", err);
      }
    };

    if (role === "user") {
      loadFolderListUser();
    }
  }, [role, id, deviceId]);

  const handleSelectChange = (folderName: string, checked: boolean) => {
    setSelectedFolders((prev) =>
      checked ? [...prev, folderName] : prev.filter((id) => id !== folderName)
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedFolders(folderList.map((d) => d.name));
    } else {
      setSelectedFolders([]);
    }
  };

  const isAllSelected =
    folderList.length > 0 && selectedFolders.length === folderList.length;

  const handleDeleteFolders = async () => {
    if (selectedFolders.length === 0) {
      alert("삭제할 폴더를 선택해주세요.");
      return;
    }

    const adminUserId = localStorage.getItem("userId") ?? "";
    const companyCode = localStorage.getItem("companyCode") ?? "";

    try {
      await Promise.all(
        selectedFolders.map(async (folderName) => {
          const folder = folderList.find((f) => f.name === folderName);
          if (!folder) return;

          console.log(adminUserId, id, deviceId, folder.rootDirectoryPath);

          const res = await fetchFilePathDelete(
            adminUserId,
            id || "", // targetUserId
            deviceId || "", // targetDeviceId
            folder.rootDirectoryPath,
            companyCode
          );

          console.log(`✅ 폴더 삭제 완료: ${folderName}`, res);
        })
      );

      alert("선택한 폴더가 삭제되었습니다.");
      setSelectedFolders([]); // ✅ 선택 초기화

      // ✅ 폴더 목록 다시 불러오기
      const refreshed = await fetchBackupFolderList(
        Number(id),
        deviceId || "",
        loginToken || ""
      );
      setFolderList(refreshed.message.rootDirectoryList);
    } catch (err) {
      console.error("🚨 폴더 삭제 실패:", err);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  return (
    <BackupFolderContainer>
      <ContentsTopHeader
        title="디바이스 목록 > 백업 파일 목록"
        backArrowPath={`/backup/device/${id}`}
      />
      <ContentsBody>
        <div className="top-components">
          <div className="l">
            {role === "admin" && (
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
                id="allSelect"
              />
            )}
            {role === "admin" ? (
              <label htmlFor="allSelect">
                전체 선택 (0 / {folderList && folderList.length})
              </label>
            ) : (
              <label htmlFor="allSelect">
                전체 {folderList && folderList.length}
              </label>
            )}
          </div>
          {role === "admin" && (
            <div className="r">
              <button
                disabled={selectedFolders.length === 0}
                onClick={handleDeleteFolders}
              >
                선택 삭제
              </button>
            </div>
          )}
        </div>
        <div className="hr"></div>
        <FolderList
          data={folderList}
          userData={userData}
          selectedFolders={selectedFolders}
          onChange={handleSelectChange}
          deviceId={deviceId}
          id={id || ""}
          userAccount={userAccount || ""}
          role={role || ""}
          userName={userName}
          department={department}
          pcName={pcName}
        />
      </ContentsBody>
    </BackupFolderContainer>
  );
};

export default BackupFolderList;
